import { useEffect, useRef, useState } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { copy, PLACES, PRICES } from './landingCopy';
import type { Language } from './landingCopy';
import './landing.css';
import './home.css';
import LandingContact from './LandingContact';
import Wordmark from './Wordmark';
import SiteBrand, { COUPONS_PATH, COUPONS_NAME, COUPONS_MORE, HOME_PATH } from './SiteBrand';
import type { ContactIntent } from './LandingContact';

const platform = 'https://www.city-seek.com';

/* Section addresses, in the order the navigation lists them. */
const ANCHORS = ['explore', 'how', 'new-in-town', 'pricing', 'work-with-us', 'story'];

/*
  The stop the demo is modelled on. Its story and audio live behind the game,
  so the links lead there: the words on the card are the real ones, and the
  button opens the real thing.
*/
/* Weißer Turm, stop 1 of Darmstadt Beta Adventure, the free city game. (The free
   trial walk these used to open was switched off on 2026-10-01.) */
const DEMO_STOP_URL = '/app/games/darmstadt-beta-adventure-9af80024/stops/75fbe148';
const FREE_GAME_URL = '/darmstadt/games/darmstadt-beta-adventure-9af80024';

/* Where each way in leads inside the product. */
const LINKS = {
 home: '/app',
 games: '/app/games',
 events: '/app/events',
 discover: '/app/explore/events',
 month: '/app/events/monthly',
 store: '/app/coupons',
 // Opens the plans sheet in the app (asks a guest to sign in first).
 plans: '/app?plans=1',
};

/* The product's API: the event calendar is public and answers this origin. */
const API = 'https://web-server.up.railway.app';

/* The next event on the calendar, for the "tonight" card. */
type LiveEvent = { slug: string; names: Record<Language, string>; venue: string; start: string; image: string };
type Copy = (typeof copy)[Language];

const berlinDay = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Berlin', year: 'numeric', month: '2-digit', day: '2-digit' }).format(d);
const LOCALES: Record<Language, string> = { en: 'en-GB', de: 'de-DE', tr: 'tr-TR' };

/** "Tonight · 20:00", "Tomorrow · 22:00" or "Sat 4 Oct · 19:00", in Darmstadt time. */
function whenLabel(iso: string, language: Language, c: Copy) {
 const start = new Date(iso);
 const time = start.toLocaleTimeString(LOCALES[language], { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: 'Europe/Berlin' });
 const day = berlinDay(start);
 const now = new Date();
 if (day === berlinDay(now)) {
  const hour = Number(new Intl.DateTimeFormat('en-GB', { hour: '2-digit', hour12: false, timeZone: 'Europe/Berlin' }).format(start));
  return `${hour >= 17 ? c.liveTonight : c.liveToday} · ${time}`;
 }
 if (day === berlinDay(new Date(now.getTime() + 86_400_000))) return `${c.liveTomorrow} · ${time}`;
 const date = start.toLocaleDateString(LOCALES[language], { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'Europe/Berlin' });
 return `${date} · ${time}`;
}

/** Euro in the reader's convention: €2.99 in English, 2,99 € otherwise. */
function euro(cents: number, language: Language) {
 const v = cents / 100;
 const s = Number.isInteger(v) ? String(v) : v.toFixed(2);
 return language === 'en' ? `€${s}` : `${s.replace('.', ',')} €`;
}

/*
  The narration the game plays at this stop, in the reader's language.

  These are the real files the product serves, so the landing page can play the
  thing itself rather than describe it. Nothing is preloaded: the English file
  alone is 1.6 MB, and a marketing page has no business spending that on
  somebody who never presses play.
*/
/* The reviewed recording the game itself plays (game.location_content.audio_url), one per language. */
const STOP_AUDIO_BASE = 'https://s3.eu-central-003.backblazeb2.com/CitySeek-all-images/audio/cityseek-darmstadt-beta-adventure';
const STOP_AUDIO_FILE = 'weisser-turm-review-20260919-final-20260920-speech-20260920.mp3';
const STOP_AUDIO: Record<Language, string> = {
 en: `${STOP_AUDIO_BASE}/en/${STOP_AUDIO_FILE}`,
 de: `${STOP_AUDIO_BASE}/de/${STOP_AUDIO_FILE}`,
 tr: `${STOP_AUDIO_BASE}/tr/${STOP_AUDIO_FILE}`,
};

/* Photographs, all of them CitySeek's own. */
const IMG = {
 herrngarten: '/images/herrngarten.webp',
 // Wikimedia Commons, LSDSL, CC BY-SA 2.0 DE: credited on the photo itself.
 tower: '/brand/photos/weisser-turm.webp',
 music: '/images/citylab-music.webp',
 coffee: '/brand/photos/coffee-mondschein.webp',
 // The CitySeek meetup formats, whole posters from the app (public/images/meetups there).
 posters: ['/brand/photos/meetup-citywalk.webp', '/brand/photos/meetup-cityhike.webp', '/brand/photos/meetup-citylens.webp', '/brand/photos/meetup-citybike.webp'],
 night: '/brand/photos/citylab-night.webp',
 space: '/brand/photos/citylab-space.webp',
 founders: ['/brand/photos/safa-portrait.webp', '/brand/photos/umut.webp'],
};

/*
  The meetup formats (CityWalk, CityHike, CityLens, CityBike) are still being
  prepared, so every one carries a "Soon" badge wherever it appears. Set this to
  false when the first dates are on the calendar.
*/
const MEETUPS_SOON = true;

/* The business page for CitySeek Coupons, where cafés and shops become partners. */
const COUPONS_PAGE = COUPONS_PATH;
// The public event list, readable without the app. The landing page is the site's
// most linked page and pointed only into the app, so search engines had no plain
// link from it to a single event.
const EVENTS_PAGE: Record<Language, string> = { en: '/events?hl=en', de: '/events', tr: '/events?hl=tr' };
const EVENTS_LABEL: Record<Language, string> = { en: 'Events in Darmstadt', de: 'Veranstaltungen in Darmstadt', tr: 'Darmstadt’ta etkinlikler' };
// The weekend guide, the plan most people come looking for.
const WEEKEND_PAGE: Record<Language, string> = { en: '/events/wochenende?hl=en', de: '/events/wochenende', tr: '/events/wochenende?hl=tr' };
const WEEKEND_LABEL: Record<Language, string> = { en: 'This weekend in Darmstadt', de: 'Am Wochenende in Darmstadt', tr: 'Bu hafta sonu Darmstadt’ta' };
// The city guides on the main site (/darmstadt/<slug>, server/src/utils/placeGuides.ts
// there): walks, sights, the riddle game, team events and newcomers. German slugs,
// with ?hl= for the other languages, like every page on the platform.
const CITY_GUIDES: { slug: string; label: Record<Language, string> }[] = [
 { slug: 'stadtfuehrung', label: { en: 'Walking tours', de: 'Stadtführung', tr: 'Şehir turu' } },
 { slug: 'sehenswuerdigkeiten', label: { en: 'Sights', de: 'Sehenswürdigkeiten', tr: 'Gezilecek yerler' } },
 { slug: 'schnitzeljagd', label: { en: 'Scavenger hunt', de: 'Schnitzeljagd', tr: 'Hazine avı' } },
 { slug: 'teamevent', label: { en: 'Team events', de: 'Teamevent', tr: 'Takım etkinliği' } },
 { slug: 'neu-in-der-stadt', label: { en: 'New in Darmstadt', de: 'Neu in Darmstadt', tr: 'Darmstadt’a yeni gelenler' } },
];
const CITY_GUIDES_LABEL: Record<Language, string> = { en: 'Explore Darmstadt', de: 'Darmstadt entdecken', tr: 'Darmstadt’ı keşfet' };
const cityGuideHref = (slug: string, language: Language) => `/darmstadt/${slug}${language === 'de' ? '' : `?hl=${language}`}`;
// The hub over all of them at /darmstadt: today, the weekend, meetups, walks and places.
const CITY_HUB_PAGE: Record<Language, string> = { en: '/darmstadt?hl=en', de: '/darmstadt', tr: '/darmstadt?hl=tr' };
const CITY_HUB_LABEL: Record<Language, string> = { en: 'What to do in Darmstadt', de: 'Was tun in Darmstadt', tr: 'Darmstadt’ta ne yapılır' };

/* Which subject the contact form opens on, per "Work with us" card. */
const WORK_INTENTS: ContactIntent[] = ['teamAdventure', 'listVenue', 'partnership'];

/** What the product calls each language. It stores words, not codes. */
const appLanguage: Record<Language, string> = { en: 'english', de: 'german', tr: 'turkish' };
const LANGS: Language[] = ['en', 'de', 'tr'];
const languageNames: Record<Language, string> = { en: 'English', de: 'Deutsch', tr: 'Türkçe' };

/*
  Carry the chosen language across to the product.

  The ?hl= parameter works immediately and across hosts. Writing the choice to
  storage is what makes it stick, because the product reads the same key.
  The chosen flag is only set when somebody actually picks one: landing on /de/
  from a German search result is not a decision, and the product treats an
  unflagged language as a guess its account may overrule.
*/
function rememberLanguage(language: Language, chosen: boolean) {
 try {
  localStorage.setItem('cityseek:language', appLanguage[language]);
  if (chosen) localStorage.setItem('cityseek:languageChosen', '1');
 } catch { /* private mode: the ?hl= on every link still carries it */ }
}
const platformUrl = (path: string, language: Language) =>
 `${platform}${path}${path.includes('?') ? '&' : '?'}hl=${language}`;

/*
  Keep short last words on the line before them.

  A heading or label that ends with one short word alone ("so far", "Park",
  "mu?") reads as a mistake, and text-wrap: balance is not in every browser a
  visitor uses. A non-breaking space between the last two words fixes it
  everywhere. Long last words are left free, or they could push the line wide.
  In "A · B · C" labels, the space before each dot is tied too, so a line never
  starts with a dot.
*/
const tie = (text: string) => text
 .replace(/ · /g, '\u00a0· ')
 .replace(/ (\S{1,8})$/, '\u00a0$1');

export default function LandingPage({ language: initialLanguage = 'en' }: { language?: Language }) {
 /*
   The language is state, not just the address: it is the same page in another
   language, so it is swapped in place and the address is rewritten underneath.
   That keeps the scroll position, and back and forward still work.
 */
 const [language, setLanguage] = useState<Language>(initialLanguage);
 const c = copy[language];
 const open = (path: string) => platformUrl(path, language);

 const pickLanguage = (next: Language) => {
  rememberLanguage(next, true);
  setLanguage(next);
  window.history.pushState({}, '', next === 'en' ? '/' : `/${next}/`);
 };
 useEffect(() => {
  const onPop = () => {
   const path = window.location.pathname.replace(/\/+$/, '');
   setLanguage(path === '/de' ? 'de' : path === '/tr' ? 'tr' : 'en');
  };
  window.addEventListener('popstate', onPop);
  return () => window.removeEventListener('popstate', onPop);
 }, []);

 const [menuOpen, setMenuOpen] = useState(false);
 const menuButton = useRef<HTMLButtonElement>(null);
 useEffect(() => {
  document.documentElement.lang = language;
  document.title = c.title;
  // Reading the page in this language is worth remembering, but it is not the
  // same as choosing it, so this one stays unflagged.
  rememberLanguage(language, false);
  const onKey = (e: KeyboardEvent) => {
   if (e.key !== 'Escape') return;
   setMenuOpen(current => {
    if (current) menuButton.current?.focus();
    return false;
   });
  };
  document.addEventListener('keydown', onKey);
  return () => document.removeEventListener('keydown', onKey);
 }, [language, c.title]);

 /*
   The business cards bring the reader to the form with the right subject
   already chosen, rather than handing them off to a mail client.
 */
 const [intent, setIntent] = useState<ContactIntent>('');
 const openContact = (next: ContactIntent) => {
  setIntent(next);
  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
 };

 /*
   Installing CitySeek from here.

   A browser hands over a single beforeinstallprompt event when it allows an
   install, and it can only be used from a real click, so it is kept the moment
   it fires. Safari never fires it, so iPhone readers get the two manual steps.
 */
 const [installPrompt, setInstallPrompt] = useState<{ prompt: () => Promise<void> } | null>(null);
 const [installed, setInstalled] = useState(false);
 const [showInstallHelp, setShowInstallHelp] = useState(false);
 const [isIos] = useState(() => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
 });
 useEffect(() => {
  const offer = (event: Event) => { event.preventDefault(); setInstallPrompt(event as never); };
  const done = () => { setInstallPrompt(null); setInstalled(true); };
  window.addEventListener('beforeinstallprompt', offer);
  window.addEventListener('appinstalled', done);
  if (window.matchMedia?.('(display-mode: standalone)').matches) setInstalled(true);
  return () => {
   window.removeEventListener('beforeinstallprompt', offer);
   window.removeEventListener('appinstalled', done);
  };
 }, []);

 /*
   The hero's three tabs: walk, go out, treat.

   They change only when the reader asks. A panel that rotates on its own moves
   while somebody is reading it, so there is no timer here.
 */
 const [tab, setTab] = useState(0);
 const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
 const onTabKey = (e: ReactKeyboardEvent<HTMLButtonElement>) => {
  const delta = e.key === 'ArrowRight' ? 1 : e.key === 'ArrowLeft' ? -1 : 0;
  if (!delta && e.key !== 'Home' && e.key !== 'End') return;
  e.preventDefault();
  const next = e.key === 'Home' ? 0 : e.key === 'End' ? 2 : (tab + delta + 3) % 3;
  setTab(next);
  tabRefs.current[next]?.focus();
 };

 /* The real narration for the first stop. */
 const audioRef = useRef<HTMLAudioElement>(null);
 const [playing, setPlaying] = useState(false);
 const [progress, setProgress] = useState(0);
 const [storyOpen, setStoryOpen] = useState(false);
 // A different tab, or a different language, means the recording stops.
 useEffect(() => {
  audioRef.current?.pause();
  setPlaying(false);
  setProgress(0);
  setStoryOpen(false);
 }, [tab, language]);
 const toggleAudio = () => {
  const el = audioRef.current;
  if (!el) return;
  if (el.paused) void el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
  else { el.pause(); setPlaying(false); }
 };

 /*
   Films.

   Nothing reaches YouTube or Instagram until the reader presses play: an embed
   that mounts on its own makes the request, and sets cookies, before anyone
   has chosen to watch, which is not what the cookie policy promises. Our own
   films play from our own bucket, and fall back to the embed if the file is
   not there.
 */
 const videos = c.videos;
 const [film, setFilm] = useState(0);
 const [videoOn, setVideoOn] = useState(false);
 const [filmMissing, setFilmMissing] = useState(false);
 useEffect(() => { setFilm(0); }, [language]);
 useEffect(() => { setVideoOn(false); setFilmMissing(false); }, [film, language]);
 const video = videos[Math.min(film, videos.length - 1)];
 const service = video.kind === 'instagram' ? 'Instagram' : 'YouTube';

 /*
   "Find tonight's plan" shows the real next event, poster and all.

   The calendar is fetched in the browser after the page is up: the prerendered
   page keeps the photograph, so search engines and slow connections see a
   finished card, and the poster replaces it only once there is one to show.
 */
 const [liveEvent, setLiveEvent] = useState<LiveEvent | null>(null);
 const [liveState, setLiveState] = useState<'loading' | 'done'>('loading');
 useEffect(() => {
  const ctrl = new AbortController();
  const timer = window.setTimeout(() => ctrl.abort(), 6000);
  fetch(`${API}/api/events?days=14`, { signal: ctrl.signal })
   .then(r => (r.ok ? r.json() : null))
   .then(data => {
    const soon = Date.now() - 60 * 60_000; // still worth showing an hour after the start
    type ApiEvent = { slug?: string; imageUrl?: string; eventStartAt?: string; eventName?: string; eventNameEn?: string; eventNameDe?: string; eventNameTr?: string; venueName?: string; placeName?: string };
    const next = ((data?.eventsByDate || []) as { events?: ApiEvent[] }[])
     .flatMap(d => d.events || [])
     .filter(e => e.slug && e.imageUrl && e.eventStartAt && Date.parse(e.eventStartAt) > soon)
     .sort((a, b) => Date.parse(a.eventStartAt!) - Date.parse(b.eventStartAt!))[0];
    if (!next) return;
    const base = next.eventName || '';
    setLiveEvent({
     slug: next.slug!, image: next.imageUrl!, start: next.eventStartAt!, venue: next.venueName || next.placeName || '',
     names: { en: next.eventNameEn || base, de: next.eventNameDe || base, tr: next.eventNameTr || base },
    });
   })
   .catch(() => { /* the tour poster stands in */ })
   .finally(() => { window.clearTimeout(timer); setLiveState('done'); });
  return () => { window.clearTimeout(timer); ctrl.abort(); };
 }, []);

 /* Pricing shows monthly first (owner decision); yearly is one tap away. */
 const [yearly, setYearly] = useState(false);
 const tiers = [null, PRICES.supporter, PRICES.circle];

 const headlineId = 'hero-title';
 const tabNames = ['walk', 'out', 'treat'];

 return <div className="cs">
  <a className="cs-skip" href="#main">{c.skip}</a>

  <header className="cs-header">
   <div className="cs-wrap cs-header-inner">
    <SiteBrand language={language} current="home" />
    <nav className="cs-nav" aria-label={c.menu}>
     {c.nav.map((label, i) => <a key={label} href={`#${ANCHORS[i]}`}>{label}</a>)}
    </nav>
    <div className="cs-header-actions">
     <div className="cs-lang" role="group" aria-label={c.languages}>
      {LANGS.map(code => (
       <button key={code} type="button" lang={code} aria-label={languageNames[code]}
        aria-pressed={code === language} onClick={() => pickLanguage(code)}>
        {code.toUpperCase()}
       </button>
      ))}
     </div>
     <a className="cs-btn cs-btn-small cs-header-cta" href={open(LINKS.home)}>{c.openApp}</a>
     <button ref={menuButton} type="button" className="cs-menu-toggle" aria-expanded={menuOpen}
      aria-controls="cs-mobile-nav" onClick={() => setMenuOpen(!menuOpen)}>
      <svg className="cs-menu-icon" viewBox="0 0 20 20" aria-hidden="true" focusable="false">{menuOpen ? <path d="M5 5l10 10M15 5 5 15" /> : <path d="M3 6h14M3 10h14M3 14h14" />}</svg>
      <span className="cs-menu-label">{menuOpen ? c.close : c.menu}</span>
     </button>
    </div>
   </div>
   <nav id="cs-mobile-nav" className="cs-mobile-nav" hidden={!menuOpen} aria-label={c.menu}>
    <div className="cs-wrap">
     {c.nav.map((label, i) => <a key={label} href={`#${ANCHORS[i]}`} onClick={() => setMenuOpen(false)}>{label}</a>)}
     <a href={COUPONS_PAGE[language]}>{COUPONS_NAME[language]}</a>
     <div className="cs-lang cs-lang-menu" role="group" aria-label={c.languages}>
      {LANGS.map(code => (
       <button key={code} type="button" lang={code} aria-pressed={code === language} onClick={() => pickLanguage(code)}>{languageNames[code]}</button>
      ))}
     </div>
     <a className="cs-btn" href={open(LINKS.home)}>{c.openApp}</a>
    </div>
   </nav>
  </header>

  <main id="main">
   {/* ---------- Hero ---------- */}
   <section className="cs-hero" aria-labelledby={headlineId}>
    <div className="cs-wrap cs-hero-grid">
     <div className="cs-hero-copy">
      <p className="cs-label">{tie(c.badge)}</p>
      <h1 id={headlineId}>
       <span>{c.headline[0]}</span>
       <span>{c.headline[1]}</span>
       <span className="cs-hero-accent">
        {c.headline[2]}
        {/* The route under the words: the logo's road, drawn once on arrival. */}
        <svg className="cs-hero-route" viewBox="0 0 400 40" preserveAspectRatio="none" aria-hidden="true" focusable="false">
         <defs>
          <mask id="cs-route-reveal" maskUnits="userSpaceOnUse">
           <path className="cs-route-reveal" d="M6 26 C 80 6, 140 38, 210 22 S 330 8, 394 24" pathLength="1" />
          </mask>
         </defs>
         <path mask="url(#cs-route-reveal)" className="cs-route-line" d="M6 26 C 80 6, 140 38, 210 22 S 330 8, 394 24" />
        </svg>
       </span>
      </h1>
      <p className="cs-hero-intro">{tie(c.intro)}</p>
      <div className="cs-hero-actions">
       <a className="cs-btn" href={open(LINKS.home)}>{c.primary}</a>
       <a className="cs-btn cs-btn-ghost" href={open(LINKS.events)}>{c.secondary}</a>
      </div>
      <p className="cs-hero-caption">
       {c.caption}
       {!installed && <>
        {' '}
        <button type="button" className="cs-inline-link" aria-expanded={installPrompt ? undefined : showInstallHelp}
         onClick={async () => {
          rememberLanguage(language, true);
          if (installPrompt) { await installPrompt.prompt(); setInstallPrompt(null); return; }
          setShowInstallHelp(help => !help);
         }}>{c.install}</button>
       </>}
      </p>
      {!installPrompt && showInstallHelp && <p className="cs-install-help">{isIos ? c.installIos : c.installAndroid}</p>}
     </div>

     <div className="cs-demo">
      <p className="cs-demo-label" id="cs-demo-label">{c.demoLabel}</p>
      <div className="cs-demo-card">
       <div className="cs-tabs" role="tablist" aria-labelledby="cs-demo-label">
        {c.tabs.map((label, i) => (
         <button key={label} type="button" role="tab" id={`cs-tab-${tabNames[i]}`}
          ref={el => { tabRefs.current[i] = el; }}
          aria-selected={tab === i} aria-controls={`cs-panel-${tabNames[i]}`} tabIndex={tab === i ? 0 : -1}
          onClick={() => setTab(i)} onKeyDown={onTabKey}>
          {label}
         </button>
        ))}
       </div>

       <div className="cs-panel" role="tabpanel" id={`cs-panel-${tabNames[tab]}`} aria-labelledby={`cs-tab-${tabNames[tab]}`} key={`${tab}-${language}`}>
        {tab === 0 && <>
         {/* A portrait photo: shown whole over a blurred copy, so the tower keeps its top. */}
         <div className="cs-panel-photo is-portrait">
          <img className="cs-photo-blur" src={IMG.tower} width="561" height="750" alt="" aria-hidden="true" />
          <img className="cs-photo-art" src={IMG.tower} width="561" height="750" alt={c.walkAlt} />
          <span className="cs-panel-stamp">{c.walkTag}</span>
          <a className="cs-photo-credit" href="https://commons.wikimedia.org/wiki/File:Wei%C3%9Fer_Turm_Darmstadt.jpg" target="_blank" rel="noopener noreferrer">
           {c.photoBy} LSDSL · CC BY-SA 2.0 DE
          </a>
         </div>
         <div className="cs-panel-body">
          <h2 className="cs-panel-title">{tie(c.walkTitle)}</h2>
          <p>{tie(c.walkText)}</p>
          <div className="cs-listen">
           <button type="button" className="cs-listen-button" aria-pressed={playing} onClick={toggleAudio}>
            <strong>{playing ? c.pause : c.listen}</strong>
            <span>{c.listenNote}</span>
           </button>
           <span className="cs-listen-bar" aria-hidden="true"><span style={{ transform: `scaleX(${progress})` }} /></span>
           <audio ref={audioRef} src={STOP_AUDIO[language]} preload="none"
            onTimeUpdate={e => { const a = e.currentTarget; if (a.duration) setProgress(a.currentTime / a.duration); }}
            onEnded={() => { setPlaying(false); setProgress(0); }} onPause={() => setPlaying(false)} />
          </div>
          <div className="cs-panel-links">
           <button type="button" className="cs-inline-link" aria-expanded={storyOpen} aria-controls="cs-demo-story"
            onClick={() => setStoryOpen(v => !v)}>{storyOpen ? c.less : c.more}</button>
           <a className="cs-inline-link" href={platform + FREE_GAME_URL + `?hl=${language}`}>{c.freeGame}</a>
          </div>
          {storyOpen && (
           <div className="cs-demo-story" id="cs-demo-story">
            <strong>{c.storyTitle}</strong>
            <p>{c.story}</p>
            <a className="cs-inline-link" href={platform + DEMO_STOP_URL + `?hl=${language}`}>{c.primary}</a>
           </div>
          )}
         </div>
        </>}

        {tab === 1 && <>
         {/* The whole posters, never cropped: a row to swipe through. */}
         <div className="cs-posters" tabIndex={0} aria-label={c.outTag}>
          {c.outItems.map(([name], i) => (
           <div key={name} className="cs-poster">
            <img src={IMG.posters[i]} width="720" height="900" alt={`${name}: ${c.posterAlt}`} loading={i > 1 ? 'lazy' : undefined} />
            {MEETUPS_SOON && <span className="cs-soon-badge">{c.soon}</span>}
           </div>
          ))}
         </div>
         <div className="cs-panel-body">
          {/* On the posters a stamp would cover their own words, so it sits here. */}
          <p className="cs-panel-tag">{c.outTag}</p>
          <h2 className="cs-panel-title">{tie(c.outTitle)}</h2>
          <dl className="cs-out-list">
           {c.outItems.map(([name, text]) => (
            <div key={name}>
             <dt>{name}{MEETUPS_SOON && <> <span className="cs-soon-badge is-inline">{c.soon}</span></>}</dt>
             <dd>{tie(text)}</dd>
            </div>
           ))}
          </dl>
          <div className="cs-panel-links">
           <a className="cs-inline-link" href={open(LINKS.month)}>{c.outLink}</a>
          </div>
         </div>
        </>}

        {tab === 2 && <>
         <div className="cs-panel-photo">
          <img src={IMG.coffee} width="960" height="639" alt={c.photoAlts[2]} />
          <span className="cs-panel-stamp">{c.treatTag}</span>
         </div>
         <div className="cs-panel-body">
          <h2 className="cs-panel-title">{tie(c.treatTitle)}</h2>
          <p>{tie(c.treatText)}</p>
          <div className="cs-panel-links">
           <a className="cs-inline-link" href={open(LINKS.store)}>{c.treatLink}</a>
          </div>
         </div>
        </>}
       </div>
      </div>
      <p className="cs-demo-note">{c.demoNote}</p>
     </div>
    </div>
   </section>

   {/* ---------- Places ribbon ---------- */}
   <div className="cs-ribbon" aria-hidden="true">
    <div className="cs-ribbon-track">
     {[0, 1].map(copyIndex => (
      <ul key={copyIndex}>{PLACES.map(place => <li key={place}>{place}</li>)}</ul>
     ))}
    </div>
   </div>

   {/* ---------- Proof ---------- */}
   <section className="cs-proof" aria-labelledby="cs-proof-title">
    <div className="cs-wrap">
     <h2 className="cs-label" id="cs-proof-title">{c.proofLabel}</h2>
     <dl className="cs-stats">
      {c.stats.map(([value, label]) => <div key={label}><dt>{tie(label)}</dt><dd>{value}</dd></div>)}
     </dl>
    </div>
   </section>

   {/* ---------- Local over global ----------
       The agenda behind the product in one statement and three short lines,
       then the people it is for, each one tap from their way in. */}
   <section className="cs-section cs-local" id="local" aria-labelledby="cs-local-title">
    <div className="cs-wrap">
     <div className="cs-local-head">
      <p className="cs-label">{c.localLabel}</p>
      <h2 id="cs-local-title">{c.localTitle.map(line => <span key={line}>{tie(line)}</span>)}</h2>
      <p className="cs-lead">{tie(c.localText)}</p>
     </div>
     <ol className="cs-local-points">
      {c.localPoints.map(([title, text], i) => (
       <li key={title}>
        <span className="cs-local-num" aria-hidden="true">0{i + 1}</span>
        <h3>{tie(title)}</h3>
        <p>{tie(text)}</p>
       </li>
      ))}
     </ol>

     <div className="cs-for">
      <p className="cs-for-title">{c.forTitle}</p>
      <ul className="cs-for-list">
       {c.audiences.map(([who, line], i) => {
        const inner = <><strong>{who}</strong><span>{line}</span></>;
        return (
         <li key={who}>
          {i === 0 && <a href={open(LINKS.discover)}>{inner}</a>}
          {i === 1 && <a href={platform + FREE_GAME_URL + `?hl=${language}`}>{inner}</a>}
          {i === 2 && <a href="#new-in-town">{inner}</a>}
          {i === 3 && <button type="button" onClick={() => openContact('listVenue')}>{inner}</button>}
          {i === 4 && <a href={COUPONS_PAGE[language]}>{inner}</a>}
         </li>
        );
       })}
      </ul>
     </div>
    </div>
   </section>

   {/* ---------- Moods ---------- */}
   <section className="cs-section" id="explore" aria-labelledby="cs-explore-title">
    <div className="cs-wrap">
     <div className="cs-heading">
      <p className="cs-label">{c.moodLabel}</p>
      <h2 id="cs-explore-title">{tie(c.moodTitle)}</h2>
      <p>{tie(c.moodIntro)}</p>
     </div>
     <div className="cs-cards">
      {c.cards.map(([title, text, link], i) => (
       <article key={title} className="cs-card">
        {i === 1 ? (liveEvent ? (
         /* The poster in full over a blurred copy of itself, so a portrait
            poster fills the landscape frame without being cropped. */
         <a className="cs-card-photo cs-card-poster" href={open(`/app/events/${liveEvent.slug}`)}
          aria-label={`${c.liveOpen}: ${liveEvent.names[language]}`}>
          <img className="cs-poster-blur" src={liveEvent.image} alt="" aria-hidden="true" />
          <img className="cs-poster-art" src={liveEvent.image} alt="" />
          <span className="cs-live-badge"><span className="cs-live-dot" aria-hidden="true" />{c.liveNext}</span>
          <span className="cs-live-meta">
           <strong>{liveEvent.names[language]}</strong>
           <span>{whenLabel(liveEvent.start, language, c)}{liveEvent.venue ? ` · ${liveEvent.venue}` : ''}</span>
          </span>
         </a>
        ) : liveState === 'loading' ? (
         /* An empty poster frame while the calendar answers, so nothing jumps. */
         <div className="cs-card-photo cs-card-poster is-loading" aria-hidden="true" />
        ) : (
         /* No event to show: the monthly tour's poster, in the same frame. */
         <a className="cs-card-photo cs-card-poster" href={open(LINKS.discover)} aria-label={c.cards[1][2]}>
          <img className="cs-poster-blur" src={IMG.posters[0]} alt="" aria-hidden="true" />
          <img className="cs-poster-art" src={IMG.posters[0]} alt={`CityWalk: ${c.posterAlt}`} />
          {MEETUPS_SOON && <span className="cs-soon-badge is-card">{c.soon}</span>}
         </a>
        )) : (
         <div className="cs-card-photo">
          <img src={[IMG.herrngarten, IMG.music, IMG.coffee][i]} width={[600, 720, 960][i]} height={[450, 956, 639][i]}
           alt={c.photoAlts[i]} loading="lazy" />
         </div>
        )}
        <div className="cs-card-body">
         <h3>{tie(title)}</h3>
         <p>{tie(text)}</p>
         <a className="cs-card-link" href={open([LINKS.games, LINKS.discover, LINKS.store][i])}>{link}</a>
        </div>
       </article>
      ))}
     </div>
    </div>
   </section>

   {/* ---------- How it works ---------- */}
   <section className="cs-section cs-how" id="how" aria-labelledby="cs-how-title">
    <div className="cs-wrap">
     <div className="cs-heading">
      <p className="cs-label">{c.howLabel}</p>
      <h2 id="cs-how-title">{tie(c.howTitle)}</h2>
     </div>
     <div className="cs-steps-wrap">
     <svg className="cs-steps-route" viewBox="0 0 1000 60" preserveAspectRatio="none" aria-hidden="true" focusable="false">
      <path d="M167 30 C 270 -2, 400 62, 500 30 S 730 -2, 833 30" />
     </svg>
     <ol className="cs-steps">
      {c.steps.map(([title, text], i) => (
       <li key={title}>
        <span className="cs-step-dot" aria-hidden="true">{i + 1}</span>
        <h3>{tie(title)}</h3>
        <p>{tie(text)}</p>
       </li>
      ))}
     </ol>
     </div>
     <a className="cs-btn" href={open(LINKS.home)}>{c.howCta}</a>
    </div>
   </section>

   {/* ---------- New in town ---------- */}
   <section className="cs-section cs-new" id="new-in-town" aria-labelledby="cs-new-title">
    <div className="cs-wrap cs-new-grid">
     <figure className="cs-new-poster">
      {/* CityWalk in front, CityHike behind it: the two a newcomer starts with. */}
      <img className="cs-new-poster-back" src={IMG.posters[1]} width="720" height="900" alt="" aria-hidden="true" loading="lazy" />
      <img src={IMG.posters[0]} width="720" height="900" alt={`CityWalk: ${c.posterAlt}`} loading="lazy" />
      {MEETUPS_SOON && <span className="cs-soon-badge is-large">{c.soon}</span>}
     </figure>
     <div className="cs-new-copy">
      <p className="cs-label">{c.newLabel}</p>
      <h2 id="cs-new-title">{tie(c.newTitle)}</h2>
      <p className="cs-lead">{(() => {
       // The phrase that matters to a newcomer, in the brand orange.
       const [before, after] = c.newText.split(c.newAccent);
       return after === undefined ? tie(c.newText) : <>{before}<span className="cs-accent">{c.newAccent}</span>{tie(after)}</>;
      })()}</p>
      <ul className="cs-chips">{c.newPoints.map(point => <li key={point}>{point}</li>)}</ul>
      <div className="cs-hero-actions">
       <a className="cs-btn" href={platform + FREE_GAME_URL + `?hl=${language}`}>{c.newPrimary}</a>
       <a className="cs-btn cs-btn-ghost" href={open(LINKS.month)}>{c.newSecondary}</a>
      </div>
     </div>
    </div>
   </section>

   {/* ---------- Pricing ----------
       The prices the app charges (PRICES in landingCopy), monthly first.
       People first, then the business side, which is mostly zeros. */}
   <section className="cs-section cs-pricing" id="pricing" aria-labelledby="cs-pricing-title">
    <div className="cs-wrap">
     <div className="cs-heading cs-pricing-heading">
      <p className="cs-label">{c.pricingLabel}</p>
      <h2 id="cs-pricing-title">{tie(c.pricingTitle)}</h2>
      <p>{tie(c.pricingIntro)}</p>
     </div>
     <div className="cs-billing" role="group" aria-label={c.pricingLabel}>
      {c.billing.map((label, i) => (
       <button key={label} type="button" aria-pressed={yearly === (i === 1)} onClick={() => setYearly(i === 1)}>{label}</button>
      ))}
     </div>
     <div className="cs-plans">
      {c.plans.map((plan, i) => {
       const price = tiers[i];
       const amount = price ? (yearly ? price.year : price.month) : 0;
       const saving = price ? Math.round((1 - price.year / (price.month * 12)) * 100) : 0;
       const alt = !price ? '\u00a0'
        : yearly ? `${c.orMonthly.replace('{p}', euro(price.month, language))} · ${c.save.replace('{n}', String(saving))}`
        : c.orYearly.replace('{p}', euro(price.year, language));
       const badge = 'badge' in plan ? plan.badge : '';
       return (
        <article key={plan.name} className={`cs-plan${i === 2 ? ' is-featured' : ''}`}>
         <div className="cs-plan-head">
          <h3>{plan.name}</h3>
          {badge && <span className="cs-plan-badge">{badge}</span>}
         </div>
         <p className="cs-plan-tag">{tie(plan.tag)}</p>
         <p className="cs-plan-price">
          <strong>{euro(amount, language)}</strong>
          <span>{price ? (yearly ? c.perYear : c.perMonth) : c.forever}</span>
         </p>
         <p className="cs-plan-alt">{alt}</p>
         <ul className="cs-plan-items">{plan.items.map(item => <li key={item}>{tie(item)}</li>)}</ul>
         {plan.note && <p className="cs-plan-note">{tie(plan.note)}</p>}
         <a className={`cs-btn${i === 2 ? '' : ' cs-btn-ghost'}`} href={open(i ? LINKS.plans : LINKS.home)}>{plan.cta}</a>
        </article>
       );
      })}
     </div>
     <p className="cs-pricing-fine">{c.pricingFine}</p>

     <div className="cs-biz">
      <h3>{tie(c.bizTitle)}</h3>
      <ul className="cs-biz-grid">
       {c.biz.map(([label, value, text], i) => (
        <li key={label}>
         {i === 2 ? (
          <a href={COUPONS_PAGE[language]}>
           <span className="cs-biz-value">{value}</span>
           <strong>{label}</strong>
           <span className="cs-biz-text">{tie(text)}</span>
          </a>
         ) : (
          <button type="button" onClick={() => openContact((['listVenue', 'listVenue', 'partnership', 'teamAdventure'] as const)[i])}>
           <span className="cs-biz-value">{value}</span>
           <strong>{label}</strong>
           <span className="cs-biz-text">{tie(text)}</span>
          </button>
         )}
        </li>
       ))}
      </ul>
      <div className="cs-biz-actions">
       <button type="button" className="cs-btn" onClick={() => openContact('partnership')}>{c.bizCta}</button>
       <a className="cs-btn cs-btn-ghost" href={COUPONS_PAGE[language]}>{COUPONS_MORE[language]}</a>
      </div>
     </div>
    </div>
   </section>

   {/* ---------- Work with us ---------- */}
   <section className="cs-section" id="work-with-us" aria-labelledby="cs-work-title">
    <div className="cs-wrap">
     <div className="cs-heading">
      <p className="cs-label">{c.workLabel}</p>
      <h2 id="cs-work-title">{tie(c.workTitle)}</h2>
     </div>
     <div className="cs-work">
      {c.work.map(([title, text, link], i) => (
       <article key={title} className={i === 0 ? 'is-featured' : undefined}>
        <h3>{tie(title)}</h3>
        <p>{tie(text)}</p>
        <button type="button" className="cs-card-link" onClick={() => openContact(WORK_INTENTS[i])}>{link}</button>
        {/* Cafés also get the whole story: how CitySeek Coupons works for them. */}
        {i === 2 && <a className="cs-card-link cs-card-link-more" href={COUPONS_PAGE[language]}>{COUPONS_MORE[language]}</a>}
       </article>
      ))}
     </div>
    </div>
   </section>

   {/* ---------- Story ---------- */}
   <section className="cs-section cs-story" id="story" aria-labelledby="cs-story-title">
    <div className="cs-wrap">
     <div className="cs-story-grid">
      <div className="cs-story-copy">
       <p className="cs-label">{c.storyLabel}</p>
       <h2 id="cs-story-title">{tie(c.storyHeading)}</h2>
       <p className="cs-lead">{tie(c.storyText)}</p>
      </div>
      <div className="cs-collage">
       {[IMG.night, IMG.music, IMG.space].map((src, i) => (
        <img key={src} className={`cs-collage-${i}`} src={src} alt={c.storyAlts[[0, 1, 3][i]]} loading="lazy"
         width={[1100, 720, 1100][i]} height={[828, 956, 828][i]} />
       ))}
      </div>
     </div>

     <div className="cs-films">
      <h3>{c.filmsTitle}</h3>
      <div className="cs-films-grid">
       <div className={`cs-film-stage ${video.kind === 'instagram' ? 'is-portrait' : ''}`}>
        {videoOn && video.src && !filmMissing ? (
         <video src={video.src} poster={video.poster} controls autoPlay playsInline
          preload="none" onError={() => setFilmMissing(true)} />
        ) : videoOn ? (
         <iframe
          title={`${c.videoNames[film]} (${service})`}
          src={video.kind === 'instagram'
           ? `https://www.instagram.com/reel/${video.id}/embed/`
           : `https://www.youtube-nocookie.com/embed/${video.id}?rel=0&modestbranding=1&playsinline=1&autoplay=1&hl=${language}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
         />
        ) : (
         <button type="button" className={`cs-film-play ${video.poster && !filmMissing ? 'has-poster' : ''}`}
          style={video.poster && !filmMissing ? { backgroundImage: `url(${video.poster})` } : undefined}
          onClick={() => setVideoOn(true)}>
          <span className="cs-film-play-label">{c.videoPlay}</span>
          <span className="cs-film-play-name">{c.videoNames[film]}</span>
         </button>
        )}
       </div>
       <div className="cs-film-list">
        <ul>
         {videos.map((v, i) => (
          <li key={`${v.kind}-${v.id}`}>
           <button type="button" aria-pressed={film === i} onClick={() => setFilm(i)}>{c.videoNames[i]}</button>
          </li>
         ))}
        </ul>
        <p className="cs-film-note">
         {(!video.src || filmMissing) && <>{c.videoNote}{' '}</>}
         <a href={video.kind === 'instagram' ? `https://www.instagram.com/reel/${video.id}/` : `https://youtu.be/${video.id}`}
          target="_blank" rel="noopener noreferrer">{c.videoOpen} {service}</a>
        </p>
       </div>
      </div>
     </div>
    </div>
   </section>

   {/* ---------- About ---------- */}
   <section className="cs-section cs-about" id="about" aria-labelledby="cs-about-title">
    <div className="cs-wrap cs-about-grid">
     <div>
      <p className="cs-label">{c.aboutLabel}</p>
      <h2 id="cs-about-title">{tie(c.aboutTitle)}</h2>
      <p className="cs-lead">{tie(c.aboutText)}</p>
     </div>
     <div className="cs-founders">
      {['Safa Kaskaya', 'Umut Bilgiç'].map((name, i) => (
       <a key={name} href={`/team/${i ? 'umut-bilgic' : 'safa-kaskaya'}`}>
        <img src={IMG.founders[i]} alt="" width="480" height="480" loading="lazy" />
        <strong>{name}</strong>
        <span>{c.roles[i]}</span>
        <span className="cs-card-link">{c.meet} {name.split(' ')[0]}</span>
       </a>
      ))}
     </div>
    </div>
   </section>

   {/* ---------- FAQ ---------- */}
   <section className="cs-section cs-faq" id="faq" aria-labelledby="cs-faq-title">
    <div className="cs-wrap cs-faq-grid">
     <h2 id="cs-faq-title">{tie(c.faqTitle)}</h2>
     <div className="cs-faq-list">
      {c.faqs.map(([q, a]) => <details key={q}><summary>{tie(q)}</summary><p>{tie(a)}</p></details>)}
     </div>
    </div>
   </section>

   <LandingContact language={language} intent={intent} />

   {/* ---------- Final call ---------- */}
   <section className="cs-final" aria-labelledby="cs-final-title">
    <div className="cs-wrap">
     <h2 id="cs-final-title">{tie(c.finalTitle)}</h2>
     <p>{tie(c.finalText)}</p>
     <div className="cs-hero-actions">
      <a className="cs-btn" href={open(LINKS.home)}>{c.primary}</a>
      <a className="cs-btn cs-btn-ghost" href="#contact">{c.contactLink}</a>
     </div>
    </div>
   </section>
  </main>

  <footer className="cs-footer">
   <div className="cs-wrap cs-footer-inner">
    <a className="cs-logo" href={HOME_PATH[language]} aria-label="CitySeek"><Wordmark /></a>
    <p>{c.rights} © {new Date().getFullYear()} CitySeek · {c.where}</p>
    <a href="mailto:info@city-seek.com">info@city-seek.com</a>
    <a href={EVENTS_PAGE[language]}>{EVENTS_LABEL[language]}</a>
    <a href={WEEKEND_PAGE[language]}>{WEEKEND_LABEL[language]}</a>
    <a href={COUPONS_PAGE[language]}>{COUPONS_NAME[language]}</a>
    <nav aria-label={CITY_GUIDES_LABEL[language]}>
     <a href={CITY_HUB_PAGE[language]}>{CITY_HUB_LABEL[language]}</a>
     {CITY_GUIDES.map(guide => <a key={guide.slug} href={cityGuideHref(guide.slug, language)}>{guide.label[language]}</a>)}
    </nav>
    <nav aria-label={c.languages}>
     <a href="/" hrefLang="en">English</a><a href="/de/" hrefLang="de">Deutsch</a><a href="/tr/" hrefLang="tr">Türkçe</a>
    </nav>
    <nav aria-label="Legal">
     {c.legal.map((label, i) => <a key={label} href={['/impressum', '/privacy', '/terms', '/cookies'][i]}>{label}</a>)}
    </nav>
   </div>
  </footer>
 </div>;
}
